/**
 * awe-cart-complete-the-look.js
 *
 * Custom element that renders "Complete the Look" product cards inside the
 * cart drawer. Products are sourced from the "shop-cart" collection and
 * embedded as JSON by Liquid — no API calls needed.
 *
 * Architecture:
 *   - Reads product data from an embedded <script type="application/json"> tag
 *   - Filters out products already in cart using data-cart-product-ids
 *   - MutationObserver re-renders when morphdom resets innerHTML
 *   - attributeChangedCallback handles cart content changes
 *   - Quick-add adds the first variant directly to cart
 *   - Dispatches cart:update after adding so Horizon re-renders
 *
 * Data attributes (set by awe-cart-complete-the-look.liquid):
 *   data-cart-product-ids  Comma-separated product IDs currently in cart
 *   data-cart-item-count   Number of items currently in cart
 */
(function () {
  'use strict';

  // ── Helpers ──────────────────────────────────────────────────────────────

  // Also escapes quotes — values are interpolated into attributes (aria-label, alt),
  // and titles like `2" Necklace Extender` would otherwise end the attribute early.
  function escapeHtml(text) {
    const d = document.createElement('div');
    d.textContent = String(text ?? '');
    return d.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  }

  // ── Custom Element ────────────────────────────────────────────────────────

  class AweCartCompleteTheLook extends HTMLElement {
    /** @type {AbortController|null} */
    #controller = null;

    /** @type {MutationObserver|null} */
    #morphObserver = null;

    #isRendering = false;

    /** @type {ResizeObserver|null} */
    #resizeObserver = null;

    static observedAttributes = ['data-cart-item-count', 'data-cart-product-ids'];

    connectedCallback() {
      this.#controller = new AbortController();
      this.#initArrows();
      this.#render();

      this.#morphObserver = new MutationObserver(() => {
        if (this.#isRendering) return;
        const container = this.querySelector('[data-products]');
        if (container && container.children.length === 0) {
          this.#render();
        }
      });
      this.#morphObserver.observe(this, { childList: true, subtree: true });
    }

    disconnectedCallback() {
      this.#morphObserver?.disconnect();
      this.#morphObserver = null;
      this.#resizeObserver?.disconnect();
      this.#resizeObserver = null;
      this.#controller?.abort();
      this.#controller = null;
    }

    attributeChangedCallback(_name, oldValue, newValue) {
      if (!this.isConnected) return;
      if (oldValue === newValue) return;
      this.#render();
    }

    // ── Data ──────────────────────────────────────────────────────────────

    #getProducts() {
      const script = this.querySelector('script[data-ctl-products]');
      if (!script) return [];
      try {
        return JSON.parse(script.textContent);
      } catch {
        return [];
      }
    }

    #getCartProductIds() {
      const ids = this.dataset.cartProductIds || '';
      return new Set(ids.split(',').filter(Boolean));
    }

    // ── Render ────────────────────────────────────────────────────────────

    #render() {
      const count = parseInt(this.dataset.cartItemCount || '0', 10);
      if (count === 0) {
        this.#hide();
        return;
      }

      const cartIds = this.#getCartProductIds();
      // Liquid only embeds in-stock variants; skip anything left without one.
      const products = this.#getProducts().filter(
        (p) => !cartIds.has(String(p.id)) && p.variants?.length > 0
      );

      if (products.length === 0) {
        this.#hide();
        return;
      }

      this.#renderProducts(products);
    }

    #renderProducts(products) {
      const container = this.querySelector('[data-products]');
      if (!container) return;

      this.#isRendering = true;
      container.innerHTML = products
        .map((product) => {
          const variant = product.variants[0];
          const imageSrc = product.featured_image || '';
          const comparePrice = variant.compare_at_price;
          const price = variant.price;
          const isOnSale = comparePrice && comparePrice > price;
          const href = `/products/${escapeHtml(product.handle)}`;

          return `
          <div class="awe-ctl__card" data-product-id="${product.id}">
            <a href="${href}" class="awe-ctl__card-link" aria-label="${escapeHtml(product.title)}">
              ${
                imageSrc
                  ? `<img class="awe-ctl__card-image" src="${escapeHtml(imageSrc)}" alt="${escapeHtml(product.title)}" loading="lazy" width="208" height="208">`
                  : `<div class="awe-ctl__card-image awe-ctl__card-image--placeholder"></div>`
              }
            </a>
            <div class="awe-ctl__card-title">${escapeHtml(product.title)}</div>
            <div class="awe-ctl__card-price">
              ${
                isOnSale
                  ? `<span class="awe-ctl__price--sale">${formatMoney(price)}</span><span class="awe-ctl__price--compare">${formatMoney(comparePrice)}</span>`
                  : `<span>${formatMoney(price)}</span>`
              }
            </div>
            <button type="button" class="awe-ctl__quick-add" data-variant-id="${variant.id}" aria-label="Add ${escapeHtml(product.title)} to cart">Add</button>
          </div>`;
        })
        .join('');
      container.dataset.count = String(products.length);
      this.#isRendering = false;

      container.addEventListener('click', this.#onProductClick, {
        signal: this.#controller?.signal,
      });

      this.#show();
      this.#resizeObserver?.observe(container);
      this.#updateArrows();
      container.scrollLeft = 0;
    }

    // ── Quick Add ────────────────────────────────────────────────────────

    #onProductClick = (event) => {
      const btn = event.target.closest('button[data-variant-id]');
      if (!btn || btn.disabled) return;
      this.#addToCart(btn);
    };

    async #addToCart(btn) {
      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      btn.disabled = true;
      const originalText = btn.textContent;
      btn.textContent = 'Adding...';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
        });

        const data = await res.json();

        if (data.status) {
          btn.textContent = originalText;
          btn.disabled = false;
          return;
        }

        btn.textContent = 'Added!';
        this.#announce(`${data.items?.[0]?.title || 'Product'} added to cart`);

        setTimeout(() => {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 1500);

        setTimeout(() => {
          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: { data: { source: 'awe-complete-the-look' } },
            })
          );
        }, 0);
      } catch (_err) {
        btn.textContent = 'Error';
        setTimeout(() => {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 1500);
      }
    }

    // ── Visibility ───────────────────────────────────────────────────────

    #show() {
      const products = this.querySelector('[data-products]');
      if (products) products.hidden = false;
      // Don't remove hidden from the element itself — A/B test controls visibility
    }

    #hide() {
      const products = this.querySelector('[data-products]');
      if (products) products.hidden = true;
    }

    // ── Arrows ───────────────────────────────────────────────────────────

    #initArrows() {
      const signal = this.#controller?.signal;

      // Delegated to the element: the buttons and row can be created after
      // upgrade, or swapped by morphdom, so nothing is bound to them directly.
      this.addEventListener(
        'click',
        (event) => {
          const arrow = event.target.closest('[data-arrow-prev], [data-arrow-next]');
          const container = this.querySelector('[data-products]');
          if (!arrow || !container) return;
          const dir = arrow.hasAttribute('data-arrow-prev') ? -1 : 1;
          container.scrollBy({ left: dir * container.clientWidth * 0.8, behavior: 'smooth' });
        },
        { signal }
      );

      // scroll doesn't bubble — listen in the capture phase.
      this.addEventListener('scroll', () => this.#updateArrows(), { capture: true, passive: true, signal });

      // First render can run before the row is laid out (scrollWidth == 0);
      // re-check once it has a size, and on viewport changes.
      this.#resizeObserver = new ResizeObserver(() => this.#updateArrows());
      this.#resizeObserver.observe(this);
    }

    #updateArrows() {
      const container = this.querySelector('[data-products]');
      const prev = this.querySelector('[data-arrow-prev]');
      const next = this.querySelector('[data-arrow-next]');

      if (!container || !prev || !next) return;

      const { scrollLeft, scrollWidth, clientWidth } = container;
      prev.disabled = scrollLeft <= 1;
      next.disabled = scrollLeft + clientWidth >= scrollWidth - 1;
    }

    // ── Accessibility ────────────────────────────────────────────────────

    #announce(message) {
      const region = this.querySelector('[data-live-region]');
      if (region) region.textContent = message;
    }
  }

  if (!customElements.get('awe-cart-complete-the-look')) {
    customElements.define('awe-cart-complete-the-look', AweCartCompleteTheLook);
  }
})();
